<script setup lang="ts">
import { useCurrency } from '@/composables/useCurrency'
import AppState, {
  contactLabelPluralLower,
  ensureClientPortalUrl,
  getExternalCalendars,
  setEditCalendarValidationData
} from '@/states/app'
import {
  ArrowNarrowRightIcon,
  ArrowNarrowUpRightIcon,
  ChevronRightIcon,
  Copy01Icon,
  RefreshCcw01Icon,
  Trash01Icon
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIAlert,
  UIButton,
  UIFormItem,
  UISelect,
  UISwitch,
  UITag,
} from '@gohighlevel/ghl-ui'
import { flatten } from 'lodash'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { CalendarType } from '../../models/calendar'
import { CATEGORY, LSA } from '../../utils/LSA'
import { copyToClipboard, getTooltip } from '../../utils/generalFunctions'
import CalendarSyncModal from '../Settings/CalendarSyncModal.vue'
import HeaderTile from '../common/HeaderTile.vue'
import Tooltip from '../common/Tooltip.vue'
import { useNotify } from '../composition/notification'
const { t } = useI18n()
const { sendNotification } = useNotify()
const { currencyOptions, getCurrencySymbol } = useCurrency()

const props = defineProps({
  showConnectionSyncModal: {
    type: Boolean,
  },
  updateShowConnectionSyncModal: {
    type: Function,
    default: () => {},
  },
})

const state = reactive({
  isMounted: false,
  isFetchingExternalCalendars: false,
  googleCalendars: [] as any[],
  googleAccounts: [] as any[],
  haveMerchant: false,
  formData: {
    selectedCalendar: '',
    syncOption: 'oneway',
    shouldExecuteAutomation: false,
    reserveService: false,
    category: '',
    service: '',
    price: undefined,
    currencyCode: 'USD',
    enableClientPortalBooking: false,
  },
  syncOptions: [
    {
      value: 'oneway',
      label: t('calendar_advanced.connections.default_one_way'),
    },
    { value: 'twoway', label: t('calendar_advanced.connections.two_way_sync') },
    {
      value: 'disable_trigger',
      label: t('calendar_advanced.connections.two_way_sync'),
    },
    { value: 'smart', label: t('calendar_advanced.connections.smart_sync') },
  ],
})
const rwgFormRef = ref()

const categoryOptions = computed(() => {
  return CATEGORY.map(category => {
    return {
      value: category,
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      label: t(`lsa.categories.${category}`),
    }
  })
})

const serviceTypeOptions = computed(() => {
  const selectedCategory = state.formData.category

  if (selectedCategory) {
    const jobTypes = LSA[selectedCategory]

    if (jobTypes) {
      return jobTypes.map(jobType => {
        return {
          value: `${jobType.vertical}-${jobType.job_type_id}`,
          // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
          label: t(`lsa.job_types.${jobType.job_type_id}`),
        }
      })
    }
  }

  return []
})

const paymentCurrencyOptions = computed(() => {
  return currencyOptions.value.map(option => {
    return {
      ...option,
      disabled: option.value !== 'USD',
    }
  })
})

onMounted(() => {
  state.isMounted = true
})

watch(
  () => state.formData.category,
  (newValue, oldValue) => {
    if (oldValue) {
      state.formData.service = ''
    }
  }
)

const editingCalendar = computed(() => {
  return AppState.editingCalendar
})
const emit = defineEmits(['onSave', 'onCancel'])

const methods = {
  async loadData() {
    // Hydrate from editingCalendar first so Save cannot persist defaults
    // while the unrelated external-calendars request is in flight or fails.
    state.formData.enableClientPortalBooking = Boolean(
      editingCalendar.value?.enableClientPortalBooking
    )
    state.formData.reserveService = Boolean(
      editingCalendar.value?.reserveService
    )
    if (
      editingCalendar.value?.homeServiceData?.categoryType &&
      editingCalendar.value?.homeServiceData?.jobType
    ) {
      state.formData.category = categoryOptions.value.find(
        category =>
          category.value ===
          editingCalendar.value?.homeServiceData?.categoryType
      )?.value as string

      state.formData.service = `${editingCalendar.value?.homeServiceData?.categoryType}-${editingCalendar.value?.homeServiceData?.jobType}`

      state.formData.price = editingCalendar.value?.price?.priceMicros
        ? String(editingCalendar.value?.price?.priceMicros / 1000000)
        : ''

      state.formData.currencyCode = editingCalendar.value?.price?.currencyCode
        ? editingCalendar.value?.price?.currencyCode
        : 'USD'
    }

    const isEventCalendar =
      editingCalendar.value?.calendarType === CalendarType.EVENT
    const hasLinkedGoogleCalendar = Boolean(
      editingCalendar.value?.linkedCalendars?.google?.id
    )
    if (isEventCalendar || hasLinkedGoogleCalendar) {
      try {
        await methods.loadExternalCalendars()
      } catch {
        // Non-fatal: Client Portal / RWG fields are already hydrated above.
      }
    }

    const googleCal = editingCalendar.value?.linkedCalendars?.google
    if (googleCal?.id) {
      state.formData.selectedCalendar = googleCal?.oauthId + '_' + googleCal?.id

      state.formData.syncOption = editingCalendar.value?.syncOption || 'oneway'
      if (editingCalendar.value?.syncOption === 'disable_trigger') {
        state.formData.syncOption = 'twoway'
        state.formData.shouldExecuteAutomation = false
      } else if (editingCalendar.value?.syncOption === 'twoway') {
        state.formData.syncOption = 'twoway'
        state.formData.shouldExecuteAutomation = true
      }
    }
  },
  async openIntegrationPage() {
    AppState.parentApp?.then(parent => {
      parent.emit('go-to-connections-settings', {
        providerId: undefined,
      })
    })
  },
  async removeGoogleCalendar() {
    if (!confirm(t('calendar_advanced.connections.confirm_remove'))) {
      return
    }

    state.formData.selectedCalendar = ''
    state.formData.syncOption = 'oneway'
    state.formData.shouldExecuteAutomation = false
  },
  async loadExternalCalendars() {
    state.isFetchingExternalCalendars = true
    try {
      await getExternalCalendars()
      const calendars = AppState.externalCalendars
      const calendarList = flatten(Object.values(calendars))

      calendarList.forEach((googleCal: any) => {
        googleCal.uid = googleCal.oauthId + '_' + googleCal.id
      })

      state.googleAccounts = calendars
      state.googleCalendars = calendarList
    } finally {
      state.isFetchingExternalCalendars = false
    }
  },
  async emitSave() {
    const selectedGoogleCalendar = state.googleCalendars.find(
      (cal: any) => cal.uid === state.formData.selectedCalendar
    )

    let reserveData: any = {}

    if (
      rwgFormRef.value &&
      state.formData.reserveService &&
      state.formData.service
    ) {
      try {
        await rwgFormRef.value.getForm().validate()
      } catch (error) {
        return
      }
      reserveData = {
        reserveService: state.formData.reserveService,
        homeServiceData: {
          categoryType: state.formData.service.split('-')[0],
          jobType: state.formData.service.split('-')[1],
        },
        price: {
          priceMicros: Math.round(Number(state.formData.price) * 1000000),
          currencyCode: state.formData.currencyCode,
        },
      }
    }

    if (selectedGoogleCalendar) {
      const googleCal = editingCalendar?.value?.linkedCalendars?.google

      let linkedCalendars: any = {}

      if (
        state.formData.selectedCalendar.includes(googleCal?.id) &&
        selectedGoogleCalendar?.oauthId?.includes(googleCal?.oauthId)
      ) {
        linkedCalendars = {
          google: {
            userId: AppState?.user?.id,
            ...googleCal,
          },
        }
      } else {
        linkedCalendars = {
          google: {
            id: selectedGoogleCalendar.id,
            userId: AppState?.user?.id,
            name: selectedGoogleCalendar.summary,
            accessRole: selectedGoogleCalendar.accessRole,
            oauthId: selectedGoogleCalendar.oauthId,
          },
        }
      }

      let syncOption = state.formData.syncOption

      if (state.formData.syncOption === 'twoway') {
        syncOption = state.formData.shouldExecuteAutomation
          ? 'twoway'
          : 'disable_trigger'
      }
      return {
        linkedCalendars,
        syncOption,
        enableClientPortalBooking: state.formData.enableClientPortalBooking,
        ...reserveData,
      }
    }
    return {
      linkedCalendars: {},
      enableClientPortalBooking: state.formData.enableClientPortalBooking,
      ...reserveData,
    }
  },
  gotoGoogleOrganicBookingSetup() {
    emit('onCancel')
    AppState.parentApp?.then(parent => {
      parent.emit('go-to-connections-settings', { tab: 'organic-booking' })
    })
  },
  handleCancel() {
    AppState.isConnectionSyncLoading = false
    props.updateShowConnectionSyncModal(false)
  },
  copyClientPortalUrl() {
    copyToClipboard(AppState.clientPortalUrl)
    sendNotification({
      type: 'info',
      title: t('settings.calendar.copied_to_clipboard'),
      icon: 'InfoCircleIcon',
      duration: 1000,
    })
  },
}

const calendarOptions = computed(() => {
  const masterOptions: any = []
  const googleAccounts = Object.keys(state.googleAccounts)
  googleAccounts.forEach(accountName => {
    const calendars = state.googleAccounts[accountName]
    masterOptions.push({
      type: 'group',
      label: `${accountName}`,
      key: `${accountName}`,
      children: calendars.map((calendar: any) => {
        return {
          label: calendar.summary,
          value: `${calendar.oauthId}_${calendar.id}`,
        }
      }),
    })
  })
  return masterOptions
})

const readOnlyAccessMessage = computed(() => {
  const selectedGoogleCalendar = state.googleCalendars.find(
    (cal: any) => cal.uid === state.formData.selectedCalendar
  )

  if (selectedGoogleCalendar) {
    if (selectedGoogleCalendar.accessRole.includes('reader')) {
      return t('calendar_advanced.connections.read_only_access')
    }
  }

  return ''
})

const syncOptionTooltip = computed(() => {
    return  t('calendar_advanced.connections.sync_option_tooltip', {
      contactsLabel: contactLabelPluralLower.value,
    })
})

defineExpose({
  methods,
})

watch(
  () => AppState.merchant,
  () => {
    if (Object.keys(AppState.merchant).length > 0) {
      state.haveMerchant = true
      setEditCalendarValidationData('haveMerchant', state.haveMerchant)
    }
  },
  {
    deep: true,
    immediate: true,
  }
)

watch(
  () => editingCalendar.value,
  () => {
    if (editingCalendar.value) {
      methods.loadData()
    }
  },
  { immediate: true }
)

watch(
  () => state.formData.enableClientPortalBooking,
  enabled => {
    if (enabled) {
      void ensureClientPortalUrl()
    }
  },
  { immediate: true }
)

const currentConnection = computed(() => {
  const { category, service, currencyCode } = state.formData
  const currencySymbol = getCurrencySymbol(currencyCode)
  const priceMicros = editingCalendar.value?.price?.priceMicros

  const categoryLabel = categoryOptions.value.find(
    c => c.value === category
  )?.label

  const serviceLabel = serviceTypeOptions.value.find(
    s => s.value === service
  )?.label

  return [
    {
      key: 'category',
      label: t('calendar_advanced.connections.category'),
      value: categoryLabel,
      show: categoryLabel,
    },
    {
      key: 'service',
      label: t('calendar_advanced.connections.service'),
      value: serviceLabel,
      show: serviceLabel,
    },
    {
      key: 'price',
      label: t('calendar_advanced.connections.price') + ' (' + currencyCode + ')',
      value: priceMicros ? `${currencySymbol}${priceMicros / 1_000_000}` : '',
      show: priceMicros,
    },
  ]
})

const isServiceOptionSelected = computed(() => {
  return (
    state.formData.reserveService &&
    (state.formData.service || state.formData.category || state.formData.price)
  )
})

const header = computed(() => {
  const isEventCalendar = editingCalendar.value?.calendarType === CalendarType.EVENT
  return isEventCalendar
    ? t('calendar_advanced.connections.connections_heading')
    : t('calendar_advanced.connections.booking_channels_heading')
})

const description = computed(() => {
  const isEventCalendar = editingCalendar.value?.calendarType === CalendarType.EVENT
  return isEventCalendar
    ? t('calendar_advanced.connections.connections_sub')
    : t('calendar_advanced.connections.booking_channels_description')
})

/** Avoid a second top divider when RWG / event sync sections are not mounted. */
const hasContentAboveClientPortal = computed(() => {
  if (editingCalendar.value?.calendarType === CalendarType.EVENT) {
    return true
  }
  return Boolean(state.haveMerchant)
})
</script>

<template>
  <div class="rounded-xl border bg-white p-5 !pr-[18px] shadow-sm">
    <HeaderTile
      id="text-only-header"
      :header="header"
      :description="description"
    />

    <div class="my-[15px] border-t border-gray-200" />

    <CalendarSyncModal
      :showModal="props.showConnectionSyncModal"
      :formData="state.formData"
      :onClickClose="methods.handleCancel"
      @on-cancel="methods.handleCancel"
      :emitSave="() => emit('onSave')"
    />

    <div
      v-if="
        editingCalendar?.calendarType !== CalendarType.EVENT &&
        state.haveMerchant
      "
    >
      <HeaderTile
        id="text-only-header"
        :header="$t('calendar_advanced.connections.reserve_with_google')"
      />

      <div class="mb-4 mt-3">
        <UIAlert
          id="reserve-with-google-alert"
          type="warning"
          :closable="false"
        >
          <template #content>
            <div
              class="whitespace-pre-line leading-6"
              v-html="
                $t('calendar_advanced.connections.rwg_deprecation_notice')
              "
            />
            <div class="flex gap-20 mt-[15px]">
              <a
                class="cursor-pointer text-sm font-semibold"
                href="https://help.leadconnectorhq.com/support/solutions/articles/155000005603-setting-up-reserve-with-google-lsa-integration-new-"
                target="_blank"
                >{{
                  $t(
                    'calendar_advanced.connections.reserve_with_google_setup'
                  )
                }}</a
              >
              <span
                class="cursor-pointer text-sm font-semibold"
                @click="methods.gotoGoogleOrganicBookingSetup"
                >{{
                  $t(
                    'calendar_advanced.connections.reserve_with_google_configure'
                  )
                }}
                <ArrowNarrowRightIcon class="ml-1 h-5 w-5"
              /></span>
            </div>
          </template>
        </UIAlert>
      </div>

      <div v-if="isServiceOptionSelected" class="mt-[15px]">
        <div class="flex flex-row gap-10">
          <div v-for="item in currentConnection" :key="item.key">
            <div v-if="item.show" class="flex flex-col items-start gap-2">
              <div class="text-sm font-medium text-gray-700">
                {{ item.label }}:
              </div>
              <UITag :type="'default'" :round="true" :bordered="false">
                {{ item.value }}
              </UITag>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="editingCalendar?.calendarType === CalendarType.EVENT">
      <div class="flex items-center">
        <UIFormItem
          :label="$t('calendar_advanced.connections.link_to_calendar')"
          path="teamMembers"
          :tooltip="
            getTooltip(
              $t('calendar_advanced.connections.link_to_calendar_tooltip')
            )
          "
        >
          <div class="flex w-[430px] flex-col space-y-2">
            <UISelect
              id="calendar-external-cal-select"
              v-model:value="state.formData.selectedCalendar"
              :filterable="true"
              :options="calendarOptions"
            />
            <div v-if="!!readOnlyAccessMessage" class="text-red-600">
              {{ readOnlyAccessMessage }}
            </div>
          </div>
        </UIFormItem>

        <UIButton
          v-if="state.formData.selectedCalendar"
          id="delete-connections-calendar"
          class="ml-[10px] text-red-600"
          :text="true"
          @click="methods.removeGoogleCalendar"
        >
          <Trash01Icon class="w-4" />
        </UIButton>

        <UIButton
          id=""
          class="ml-4 text-blue-600"
          :text="true"
          @click="methods.loadExternalCalendars"
        >
          <RefreshCcw01Icon
            :class="{ 'animate-spin': state.isFetchingExternalCalendars }"
            class="w-4"
          />
        </UIButton>
      </div>
      <div class="flex flex-col space-y-2">
        <div class="flex flex-row items-center space-x-3">
          <div class="text-md font-medium text-gray-700">
            {{ $t('calendar_advanced.connections.sync_option') }}
          </div>
          <Tooltip
              :message="syncOptionTooltip"
            />
        </div>
        <div
          class="flex h-[40px] w-full max-w-[430px] flex-row items-center justify-between rounded-lg border border-gray-300 p-[12px] shadow-sm"
        >
          <div class="text-md font-normal text-gray-900">
            {{
              state.syncOptions.find(
                option => option.value === state.formData.syncOption
              )?.label
            }}
          </div>
          <UIButton
            :text="true"
            @click="() => props.updateShowConnectionSyncModal(true)"
          >
            <div class="flex flex-row items-center space-x-2">
              <div class="text-sm font-normal text-primary-600">
                {{ $t('calendar_advanced.connections.change') }}
              </div>
              <ChevronRightIcon class="h-5 w-5 text-primary-600" />
            </div>
          </UIButton>
        </div>
      </div>

      <div
        class="flex cursor-pointer items-center space-x-2 pt-4"
        @click="methods.openIntegrationPage"
      >
        <div class="text-sm font-medium text-primary-700">
          {{ $t('calendar_advanced.connections.setup_external') }}
        </div>

        <ArrowNarrowUpRightIcon class="h-5 w-5 text-blue-600" />
      </div>
    </div>

    <div
      :class="
        hasContentAboveClientPortal
          ? 'my-[15px] border-t border-gray-200 pt-[15px]'
          : 'pt-[15px]'
      "
    >
      <div class="font-medium text-gray-900">
        {{ $t('calendar_advanced.connections.client_portal_booking') }}
      </div>
      <div class="text-xs font-normal leading-6 text-gray-600">
        {{
          $t(
            'calendar_advanced.connections.client_portal_booking_description'
          )
        }}
      </div>
      <div class="flex items-center space-x-3 pb-3 pt-4">
        <div id="enable-client-portal-booking-toggle">
          <UISwitch
            v-model:value="state.formData.enableClientPortalBooking"
            :aria-label="
              t('calendar_advanced.connections.client_portal_booking')
            "
            size="small"
          />
        </div>
        <div class="font-normal text-gray-700">
          {{ $t('calendar_advanced.connections.client_portal_booking') }}
        </div>
      </div>
      <div
        v-if="state.formData.enableClientPortalBooking && AppState.clientPortalUrl"
        class="pt-2"
      >
        <div class="font-medium text-gray-900">
          {{ $t('calendar_advanced.connections.client_portal_url') }}
        </div>
        <div class="mt-2 flex items-center gap-2">
          <a
            id="client-portal-url-link"
            :href="AppState.clientPortalUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="break-all text-sm text-primary-600 hover:underline"
          >
            {{ AppState.clientPortalUrl }}
          </a>
          <button
            id="copy-client-portal-url"
            type="button"
            class="inline-flex shrink-0 items-center text-primary-600"
            :aria-label="$t('settings.calendar.copy')"
            @click="methods.copyClientPortalUrl"
          >
            <Copy01Icon class="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
